export type CanvasAspectPreset = "horizontal" | "vertical" | "square";

export const LAYOUT_TEMPLATE_TYPES = [
    "Camera",
    "Zoom",
    "Screen",
    "Media",
    "Multicam",
    "Intro",
    "Outro",
    "Chapter",
    "Speaker",
    "Captions",
    "Text",
    "List",
    "Paragraph",
    "Quote",
    "Big fact",
    "Audiogram",
    "Clip",
    "Other",
] as const;

export type LayoutTemplateType = (typeof LAYOUT_TEMPLATE_TYPES)[number] | string;

export type LayoutPreview = {
    muxPlaybackId?: string;
    thumbnailUrl?: string;
    objectFit?: "cover" | "contain";
};

export type LayoutTemplate = {
    id: string;
    title: string;
    type: LayoutTemplateType;
    previewsByPreset?: Partial<Record<CanvasAspectPreset, LayoutPreview>>;
};

export type MulticamVariant = {
    key: string;
    label: string;
    muxPlaybackId?: string;
    thumbnailUrl?: string;
};

export type LayoutPack = {
    id: string;
    title: string;
    templates: LayoutTemplate[];
    pickerEntriesByPreset: Record<CanvasAspectPreset, string[]>;
    multicamVariantsByPreset?: Partial<Record<CanvasAspectPreset, MulticamVariant[]>>;
    defaultMulticamVariantKeyByPreset?: Partial<Record<CanvasAspectPreset, string>>;
};

export const DEFAULT_LAYOUT_PACK_ID = "builtin";

export function normalizeLayoutId(layoutId: string): string {
    return String(layoutId ?? "").trim().toLowerCase();
}

export function getLayoutKindFromLayoutId(layoutId: string | null | undefined): string | null {
    const normalized = normalizeLayoutId(String(layoutId ?? ""));
    if (!normalized) return null;
    return normalized.split(":")[0] ?? null;
}

export function parseMulticamVariantKeyFromLayoutId(layoutId: string | null | undefined): string | null {
    const normalized = normalizeLayoutId(String(layoutId ?? ""));
    if (!normalized.startsWith("multicam")) return null;
    const parts = normalized.split(":");
    const raw = String(parts[1] ?? "").trim();
    return raw || null;
}

export function parseLayoutRef(layoutRef: string | null | undefined): { packId: string; layoutId: string } | null {
    const raw = String(layoutRef ?? "").trim();
    if (!raw) return null;

    const slash = raw.indexOf("/");
    if (slash <= 0 || slash >= raw.length - 1) {
        return { packId: DEFAULT_LAYOUT_PACK_ID, layoutId: raw };
    }

    const packId = raw.slice(0, slash).trim();
    const layoutId = raw.slice(slash + 1).trim();
    if (!packId || !layoutId) {
        return { packId: DEFAULT_LAYOUT_PACK_ID, layoutId: raw };
    }

    return { packId, layoutId };
}

export function formatLayoutRef(packId: string, layoutId: string): string {
    const p = String(packId ?? "").trim() || DEFAULT_LAYOUT_PACK_ID;
    const l = String(layoutId ?? "").trim();
    return `${p}/${l}`;
}

export function getLayoutTemplate(pack: LayoutPack, layoutId: string): LayoutTemplate | null {
    const id = String(layoutId ?? "").trim();
    if (!id) return null;
    const exact = pack.templates.find((t) => String(t.id) === id);
    return exact ?? null;
}

// --- Built-in pack (current hardcoded layouts + previews) ---

const HORIZONTAL_CAMERA_PLAYBACK_ID = "cfYi01E8uy8xsIsDP8PjO73IAu00X9DYwDYGkimzlhK8E";
const HORIZONTAL_ZOOM_PLAYBACK_ID = "RWExvnm8W01zoECTwSmUOm56su97028dTkXQtvUev1Dak";
const HORIZONTAL_SCREEN_PLAYBACK_ID = "G02VZ8vMeWC1pHcWSDKDm01WBT02d1utzeV7tCoygnb012c";
const HORIZONTAL_MEDIA_PLAYBACK_ID = "PjJ8lvt01mRqayNaxUiZGkPMTKFd9MYkFRtZaPBjVq2s";
const HORIZONTAL_CHAPTER_PLAYBACK_ID = "gabmGWT6fYwzy1yYNTwg44X2b3vfiyU284e8LKQ29wc";
const HORIZONTAL_INTRO_PLAYBACK_ID = "Rj023oQFLD4ozU2y4QAplsNjhxViJeHtPQpHH67wm8fE";

const VERTICAL_ZOOM_PLAYBACK_ID = "L01exsl8vrllyzNqxc7CetZnLE01DFn02ACK6khDBLNFqg";
const VERTICAL_CAPTIONS_PLAYBACK_ID = "01WaJrchFte006HA1O8P3Qq2THmbHz2rlFyQ6kKFB5V01U";
const VERTICAL_SPEAKER_PLAYBACK_ID = "z9NMRSgZNugYfMlYyiYsIm4NQq9F01u86c4nz1NGW9yg";
const VERTICAL_MEDIA_PLAYBACK_ID = "PUmlDDecG2ysnB7BPdpxT7U9DChVDUv81az5CaF3gfE";
const VERTICAL_CAMERA_PLAYBACK_ID = "01CYX8tKVU9sC8XHJh6YUT2n1BGcVzNUKS4GFwI9nbZY";
const VERTICAL_CHAPTER_PLAYBACK_ID = "HfrMT02rrPlRcfNfwonVQjUMw2BeLPS77qdvVxUW02DUA";

const SQUARE_ZOOM_PLAYBACK_ID = "i02ZOPl02ftQUZ5ORUiNUrLLhMX3pNfq5JJhPjihXu018U";
const SQUARE_MEDIA_PLAYBACK_ID = "47zpB02xlOH9RCj3t0000WwY00vnXQNKSjle9GOXV23wQuU";
const SQUARE_CAPTIONS_PLAYBACK_ID = "N01P8wokQuDVFd5fJ7201SRMKc01lltWnfOfv02KjyKeATk";
const SQUARE_SPEAKER_PLAYBACK_ID = "lvxKerDcXqr7g77FJ92IEL4p01BMilZPASqfbfiIxYno";
const SQUARE_CAMERA_PLAYBACK_ID = "701LlqKz9iiivs02en12kzYfbzDKLtOlshDfmxNvVWp800";

// Multicam variants
const VERTICAL_MULTICAM_TWO_PLAYBACK_ID = "yt3A6A02pNelZqz9LU001Yv4b8S01li3Bb5JOb2khjyOAw";
const VERTICAL_MULTICAM_THREE_PLAYBACK_ID = "02s9ZgDa7hN02dJdmwCzxYjzCv6xlKoOlacLYY01OlyuEA";
const VERTICAL_MULTICAM_FOUR_PLAYBACK_ID = "axXhxyLwb00W7yVhz02hdxJTYDylILxWgxcbRf7flZxac";

const SQUARE_MULTICAM_TWO_PLAYBACK_ID = "O8rqTx3tXqkjVOj6XcJXn3l7XRc2ZG7IDzCWhVoEz6k";
const SQUARE_MULTICAM_THREE_PLAYBACK_ID = "01RSMLbLUY4ZAJa6B8VZM7oV4Z36n59bTE4MG1njptpM";
const SQUARE_MULTICAM_FOUR_PLAYBACK_ID = "FqUf00Jc1YIfwbzqH3xmlFwyCtiqOeMeU01MBIEiYG01PQ";

const HORIZONTAL_MULTICAM_TWO_SCREEN_PLAYBACK_ID = "UDY53A02GZD01Wc302OQxGRWYTVg6YfKslX4G8qLrr3wmk";
const HORIZONTAL_MULTICAM_TWO_PLAYBACK_ID = "WNSfd02pbwkKBKVoLVdsS02zQFWMbJAy302AEbDRChOFY00";
const HORIZONTAL_MULTICAM_THREE_SCREEN_PLAYBACK_ID = "fjWYIdFqWkR00U9WTatvSwg00OpuqJ91Jn02VF8dENttJA";
const HORIZONTAL_MULTICAM_SIX_PLAYBACK_ID = "woO01pcmDy029G1BCwoRwMKQ02T9M6fvo2LALV6nLFGFfM";
const HORIZONTAL_MULTICAM_THREE_PLAYBACK_ID = "e01f7hN02Hxn7QjuUou1Icqm3UVa4EJncZ3th02wX02Kjho";
const HORIZONTAL_MULTICAM_FIVE_PLAYBACK_ID = "7nbnsLnajTVEUPA2y013Oi028c84dJUTJV75TUs7uJTzc";
const HORIZONTAL_MULTICAM_FOUR_PLAYBACK_ID = "AxvR5osnQbzzquvFEn2XHdAaiZo7ZNgu00AJiDod3KFU";
const HORIZONTAL_MULTICAM_FOUR_SCREEN_PLAYBACK_ID = "i00MBb443tJiMl57cIrVEBThM5urb1qvxuHkp8VOmDWQ";

export const BUILTIN_LAYOUT_PACK: LayoutPack = {
    id: DEFAULT_LAYOUT_PACK_ID,
    title: "Default",
    templates: [
        {
            id: "camera",
            title: "Camera",
            type: "Camera",
            previewsByPreset: {
                horizontal: { muxPlaybackId: HORIZONTAL_CAMERA_PLAYBACK_ID, objectFit: "cover" },
                vertical: { muxPlaybackId: VERTICAL_CAMERA_PLAYBACK_ID, objectFit: "contain" },
                square: { muxPlaybackId: SQUARE_CAMERA_PLAYBACK_ID, objectFit: "contain" },
            },
        },
        {
            id: "zoom",
            title: "Zoom",
            type: "Zoom",
            previewsByPreset: {
                horizontal: { muxPlaybackId: HORIZONTAL_ZOOM_PLAYBACK_ID, objectFit: "cover" },
                vertical: { muxPlaybackId: VERTICAL_ZOOM_PLAYBACK_ID, objectFit: "contain" },
                square: { muxPlaybackId: SQUARE_ZOOM_PLAYBACK_ID, objectFit: "contain" },
            },
        },
        {
            id: "screen",
            title: "Screen",
            type: "Screen",
            previewsByPreset: {
                horizontal: { muxPlaybackId: HORIZONTAL_SCREEN_PLAYBACK_ID, objectFit: "cover" },
            },
        },
        {
            id: "media",
            title: "Media",
            type: "Media",
            previewsByPreset: {
                horizontal: { muxPlaybackId: HORIZONTAL_MEDIA_PLAYBACK_ID, objectFit: "cover" },
                vertical: { muxPlaybackId: VERTICAL_MEDIA_PLAYBACK_ID, objectFit: "contain" },
                square: { muxPlaybackId: SQUARE_MEDIA_PLAYBACK_ID, objectFit: "contain" },
            },
        },
        {
            id: "multicam",
            title: "Multicam",
            type: "Multicam",
        },
        {
            id: "chapter",
            title: "Chapter",
            type: "Chapter",
            previewsByPreset: {
                horizontal: { muxPlaybackId: HORIZONTAL_CHAPTER_PLAYBACK_ID, objectFit: "cover" },
                vertical: { muxPlaybackId: VERTICAL_CHAPTER_PLAYBACK_ID, objectFit: "contain" },
            },
        },
        {
            id: "intro",
            title: "Intro",
            type: "Intro",
            previewsByPreset: {
                horizontal: { muxPlaybackId: HORIZONTAL_INTRO_PLAYBACK_ID, objectFit: "cover" },
            },
        },
        {
            id: "speaker",
            title: "Speaker",
            type: "Speaker",
            previewsByPreset: {
                vertical: { muxPlaybackId: VERTICAL_SPEAKER_PLAYBACK_ID, objectFit: "contain" },
                square: { muxPlaybackId: SQUARE_SPEAKER_PLAYBACK_ID, objectFit: "contain" },
            },
        },
        {
            id: "captions",
            title: "Captions",
            type: "Captions",
            previewsByPreset: {
                vertical: { muxPlaybackId: VERTICAL_CAPTIONS_PLAYBACK_ID, objectFit: "contain" },
                square: { muxPlaybackId: SQUARE_CAPTIONS_PLAYBACK_ID, objectFit: "contain" },
            },
        },
    ],
    pickerEntriesByPreset: {
        vertical: ["zoom", "camera", "speaker", "media", "multicam", "captions", "chapter"],
        square: ["zoom", "media", "captions", "speaker", "multicam", "camera"],
        horizontal: ["camera", "zoom", "screen", "multicam", "media", "chapter", "intro"],
    },
    multicamVariantsByPreset: {
        vertical: [
            { key: "two", label: "Two", muxPlaybackId: VERTICAL_MULTICAM_TWO_PLAYBACK_ID },
            { key: "three", label: "Three", muxPlaybackId: VERTICAL_MULTICAM_THREE_PLAYBACK_ID },
            { key: "four", label: "Four", muxPlaybackId: VERTICAL_MULTICAM_FOUR_PLAYBACK_ID },
        ],
        square: [
            { key: "two", label: "Two", muxPlaybackId: SQUARE_MULTICAM_TWO_PLAYBACK_ID },
            { key: "three", label: "Three", muxPlaybackId: SQUARE_MULTICAM_THREE_PLAYBACK_ID },
            { key: "four", label: "Four", muxPlaybackId: SQUARE_MULTICAM_FOUR_PLAYBACK_ID },
        ],
        horizontal: [
            { key: "two", label: "Two", muxPlaybackId: HORIZONTAL_MULTICAM_TWO_PLAYBACK_ID },
            { key: "two_screen", label: "Two + Screen", muxPlaybackId: HORIZONTAL_MULTICAM_TWO_SCREEN_PLAYBACK_ID },
            { key: "three", label: "Three", muxPlaybackId: HORIZONTAL_MULTICAM_THREE_PLAYBACK_ID },
            { key: "three_screen", label: "Three + Screen", muxPlaybackId: HORIZONTAL_MULTICAM_THREE_SCREEN_PLAYBACK_ID },
            { key: "four", label: "Four", muxPlaybackId: HORIZONTAL_MULTICAM_FOUR_PLAYBACK_ID },
            { key: "four_screen", label: "Four + Screen", muxPlaybackId: HORIZONTAL_MULTICAM_FOUR_SCREEN_PLAYBACK_ID },
            { key: "five", label: "Five", muxPlaybackId: HORIZONTAL_MULTICAM_FIVE_PLAYBACK_ID },
            { key: "six", label: "Six", muxPlaybackId: HORIZONTAL_MULTICAM_SIX_PLAYBACK_ID },
        ],
    },
    defaultMulticamVariantKeyByPreset: {
        vertical: "two",
        square: "two",
        horizontal: "two",
    },
};

// Packs are loaded from Firestore via `LayoutPacksStore`. `BUILTIN_LAYOUT_PACK` remains as a safe fallback/seed.
